import React from 'react';
import type { ProposalPreview } from '../api';

interface Props {
  preview?: ProposalPreview;
  isLoading?: boolean;
  proposalCodigo?: string;
}

const LABELS: Record<string, string> = {
  'proposta.status': 'Proposta com status "Aceita"',
  'proposta.cliente': 'Proposta do mesmo cliente',
  'proposta.responsavelComercial': 'Responsável comercial na proposta',
  'proposta.baseComercial': 'Base comercial na versão aceita',
  'cliente.situacaoCadastral': 'Cliente ativo',
};

/**
 * Painel de geração (RN-COM-003). Mostra o que a proposta está suprindo,
 * o que ainda bloqueia a geração, e o que a modelagem do contrato ainda
 * não persiste — nada aqui é decorativo: cada linha vem do backend.
 */
export function ProposalOrigemBanner({
  preview,
  isLoading,
  proposalCodigo,
}: Props): React.ReactElement | null {
  if (isLoading && !preview) {
    return (
      <p className="text-xs text-gray-500">Verificando as condições da proposta…</p>
    );
  }
  if (!preview) return null;

  const imported = Object.entries(preview.fields).filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim() !== '',
  );

  return (
    <div
      className={`rounded-lg border p-3 text-xs space-y-2 ${
        preview.canGenerate ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            preview.canGenerate ? 'bg-emerald-500' : 'bg-amber-500'
          }`}
        />
        <span className="font-medium text-gray-800">
          {preview.canGenerate ? 'Pronto para gerar' : 'Geração bloqueada'}
          {proposalCodigo ? ` — ${proposalCodigo}` : ''}
        </span>
        {preview.versionNumero && (
          <span className="text-gray-500">versão {preview.versionNumero}</span>
        )}
      </div>

      {!preview.canGenerate && preview.blockedBy.length > 0 && (
        <ul className="space-y-0.5 text-amber-900">
          {preview.blockedBy.map((b) => (
            <li key={b.field} className="flex gap-1.5">
              <span aria-hidden>•</span>
              <span>
                {LABELS[b.field] ?? b.field} — {b.actual ? `encontrado: ${b.actual}` : 'ausente'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {imported.length > 0 && (
        <div>
          <p className="font-medium text-gray-700">A proposta supre:</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {imported.map(([field]) => (
              <span
                key={field}
                className="inline-flex items-center rounded bg-white px-1.5 py-0.5 text-[11px] text-gray-700 ring-1 ring-gray-200"
                title={`Valor preenchido a partir da proposta para "${field}". Se você digitar aqui, o seu valor é mantido e a divergência fica registrada no histórico.`}
              >
                {field}
              </span>
            ))}
          </div>
        </div>
      )}

      {preview.deferred.length > 0 && (
        <p className="text-gray-500">
          Vêm da proposta, mas ainda não são persistidos no contrato e ficam só no retrato
          gravado na 1ª versão: {preview.deferred.join(', ')}.
        </p>
      )}
    </div>
  );
}
